<script setup>
import { onMounted, ref } from "vue";
import { onBeforeRouteUpdate, useRoute, useRouter } from "vue-router";
import VacancyService from "@/services/VacancyService";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { useVacancyStore } from "@/stores/VacancyStore";

const route = useRoute();
const router = useRouter();
const vacancyService = new VacancyService();
const breadcrumbStore = useBreadcrumbStore();
const vacancyStore = useVacancyStore();

const vacancy = ref(null);
const loading = ref(false);
const saving = ref(false);
const updatingStatus = ref(false);
const convertingToStaff = ref(false);
const saveMessage = ref("");
const notes = ref("");
const status = ref("new");
const statuses = ["new", "reviewing", "parked", "converted"];

const applicantName = (val) => {
  const full = `${val?.first_name || ""} ${val?.last_name || ""}`.trim();
  return full || val?.contact?.name || "Applicant";
};

const loadVacancy = async (id) => {
  if (!id) {
    return;
  }
  loading.value = true;
  saveMessage.value = "";
  try {
    const data = await vacancyService.show(id);
    vacancy.value = data;
    status.value = data?.status || "new";
    notes.value = data?.notes || "";
    breadcrumbStore.setValue(applicantName(data));
  } finally {
    loading.value = false;
  }
};

const saveNotes = async () => {
  if (!vacancy.value?.id) {
    return;
  }
  saving.value = true;
  saveMessage.value = "";
  try {
    const data = await vacancyService.update(vacancy.value.id, {
      notes: notes.value,
    });
    vacancy.value = data;
    saveMessage.value = "Notes saved.";
  } catch (error) {
    saveMessage.value = error?.response?.data?.message || "Unable to save.";
  } finally {
    saving.value = false;
  }
};

const saveStatus = async () => {
  if (!vacancy.value?.id || updatingStatus.value) {
    return;
  }

  updatingStatus.value = true;
  saveMessage.value = "";
  try {
    const data = await vacancyService.update(vacancy.value.id, {
      status: status.value,
    });
    vacancy.value = data;
    status.value = data?.status || status.value;
    vacancyStore.fetchNewCount({ force: true });
    saveMessage.value = `Status moved to ${status.value}.`;
  } catch (error) {
    status.value = vacancy.value?.status || "new";
    saveMessage.value = error?.response?.data?.message || "Unable to update status.";
  } finally {
    updatingStatus.value = false;
  }
};

const convertToStaff = async () => {
  if (!vacancy.value?.id || convertingToStaff.value) {
    return;
  }

  const confirmed = window.confirm(
    "Convert this vacancy into a staff record? This will remove the vacancy entry."
  );
  if (!confirmed) {
    return;
  }

  convertingToStaff.value = true;
  saveMessage.value = "";
  try {
    const data = await vacancyService.convertToStaff(vacancy.value.id);
    vacancyStore.fetchNewCount({ force: true });
    const staffId = data?.staff?.id;
    if (!staffId) {
      saveMessage.value = "Staff was created but could not be opened.";
      return;
    }
    router.push(`/staff/${staffId}`);
  } catch (error) {
    saveMessage.value = error?.response?.data?.message || "Unable to convert vacancy to staff.";
  } finally {
    convertingToStaff.value = false;
  }
};

onMounted(() => {
  loadVacancy(route.params.id);
});

onBeforeRouteUpdate((to, from, next) => {
  if (to.params.id && to.params.id !== route.params.id) {
    loadVacancy(to.params.id);
  }
  next();
});
</script>

<template>
  <div v-if="loading" class="box">Loading vacancy...</div>
  <div v-else-if="vacancy">
    <div class="box mb-4">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 class="text-xl font-semibold">{{ applicantName(vacancy) }}</h1>
        <div class="flex items-center gap-2">
          <router-link
            v-if="vacancy.ticket?.id"
            class="btn"
            :to="`/tickets/${vacancy.ticket.id}`"
          >
            Open Ticket #{{ vacancy.ticket.id }}
          </router-link>
          <button class="btn accent" :disabled="saving" @click.prevent="saveNotes">
            {{ saving ? "Saving..." : "Save Notes" }}
          </button>
          <button class="btn save" :disabled="convertingToStaff" @click.prevent="convertToStaff">
            {{ convertingToStaff ? "Converting..." : "Convert to Staff" }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><b>Status:</b></div>
        <div>
          <select v-model="status" class="w-full" :disabled="updatingStatus" @change="saveStatus">
            <option v-for="item in statuses" :key="item" :value="item">
              {{ item }}
            </option>
          </select>
          <p class="text-xs mt-1 text-slate-500">Changing status saves immediately.</p>
        </div>

        <div><b>Position:</b></div>
        <div>{{ vacancy.position || "-" }}</div>

        <div><b>Email:</b></div>
        <div>{{ vacancy.email || vacancy.contact?.email || "-" }}</div>

        <div><b>Phone:</b></div>
        <div>{{ vacancy.phone || vacancy.contact?.phone || "-" }}</div>

        <div><b>City:</b></div>
        <div>{{ vacancy.city || "-" }}</div>

        <div><b>Postcode:</b></div>
        <div>{{ vacancy.postcode || "-" }}</div>

        <div><b>Date of birth:</b></div>
        <div>{{ vacancy.date_of_birth || "-" }}</div>

        <div><b>NI number:</b></div>
        <div>{{ vacancy.national_insurance || "-" }}</div>

        <div><b>Driving licence:</b></div>
        <div>{{ vacancy.driving_licence || "-" }}</div>

        <div><b>Current employer:</b></div>
        <div>{{ vacancy.current_employer || "-" }}</div>

        <div><b>Previous role:</b></div>
        <div>{{ vacancy.previous_role || "-" }}</div>

        <div><b>Years experience:</b></div>
        <div>{{ vacancy.years_experience || "-" }}</div>

        <div><b>Criminal record:</b></div>
        <div>{{ vacancy.criminal_record || "-" }}</div>
      </div>
    </div>

    <div class="box mb-4">
      <h2 class="text-lg font-semibold mb-2">Notes</h2>
      <textarea
        v-model="notes"
        class="w-full border p-2 h-40"
        placeholder="Internal notes for processing this application"
      ></textarea>
      <p v-if="saveMessage" class="text-sm mt-2">{{ saveMessage }}</p>
    </div>

    <div class="box mb-4">
      <h2 class="text-lg font-semibold mb-2">Application Details</h2>
      <div class="whitespace-pre-wrap" v-if="vacancy.qualifications">
        <b>Qualifications</b>
        <p>{{ vacancy.qualifications }}</p>
      </div>
      <div class="whitespace-pre-wrap mt-3" v-if="vacancy.relevant_experience">
        <b>Relevant experience</b>
        <p>{{ vacancy.relevant_experience }}</p>
      </div>
      <div class="whitespace-pre-wrap mt-3" v-if="vacancy.why_apply">
        <b>Why apply</b>
        <p>{{ vacancy.why_apply }}</p>
      </div>
      <div class="whitespace-pre-wrap mt-3" v-if="vacancy.availability">
        <b>Availability</b>
        <p>{{ vacancy.availability }}</p>
      </div>
      <div class="whitespace-pre-wrap mt-3" v-if="vacancy.reference_details">
        <b>References</b>
        <p>{{ vacancy.reference_details }}</p>
      </div>
    </div>

    <div class="box">
      <h2 class="text-lg font-semibold mb-2">Raw Ticket Text</h2>
      <pre class="whitespace-pre-wrap">{{ vacancy.raw_text || "-" }}</pre>
    </div>
  </div>
  <div v-else class="box">Vacancy not found.</div>
</template>
